// --- Dependencies --------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import type { TyApiResponse, TyBranch } from "@/types";
import useFetch from "@/hooks/useFetch";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import ActionsBar from "@/UI/components/ActionsBar";
import BranchCard from "@/UI/cards/BranchCard";
import Skeleton from "@/UI/atoms/Skeleton";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

export default function BranchesScreen() {
    const t = useTranslation();
    const navigate = useNavigate();
    const { addComp, removeComp } = usePopup();
    const { canEditBranch } = usePermission();

    const { data, loading, error, invoke } = useFetch<TyApiResponse<{ branches: TyBranch[]; totalAllowed: number }>>("/branches");

    const branches = [...(data?.data?.branches ?? [])].sort((a, b) => {
        const aAccess = canEditBranch(a._id) ? 0 : 1;
        const bAccess = canEditBranch(b._id) ? 0 : 1;
        return aAccess - bAccess;
    });

    // --- Reset overrides -------------------------------------------------------------------------

    const [resettingId, setResettingId] = useState<string | null>(null);

    const { data: resetData, invoke: invokeReset } = useFetch<TyApiResponse<Record<string, never>>>(
        `/branches/${resettingId ?? "x"}/overrides`,
        { method: "DELETE" },
        false
    );

    useEffect(() => {
        if (!resettingId) return;
        invokeReset();
    }, [resettingId]);

    useEffect(() => {
        if (!resetData?.success) return;
        setResettingId(null);
        invoke();
    }, [resetData]);

    // --- Handlers --------------------------------------------------------------------------------

    const handleResetPrompt = (branch: TyBranch) => {
        removeComp("confirm-reset-branch");
        addComp({
            name: "confirm-reset-branch",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: {
                message: t.branches.resetConfirmMessage,
                icon: "/icons/cancel-icon.svg",
                variant: "danger",
                confirmLabel: t.branches.resetConfirmLabel,
                onConfirm: () => setResettingId(branch._id),
            },
        });
    };

    // --- Render ----------------------------------------------------------------------------------

    return (
        <div className="branches-screen">
            <ActionsBar title={t.branches.title} />

            <div className="branches-screen-content">
                {loading && (
                    <div className="branches-screen-grid">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} className="branches-screen-skeleton" />
                        ))}
                    </div>
                )}

                {error && <p className="branches-screen-message branches-screen-message--error">{error}</p>}

                {!loading && !error && branches.length === 0 && (
                    <div className="branches-screen-empty">
                        <div className="branches-screen-empty-icon">
                            <img src="/icons/side-bar/branches-icon-dark.svg" alt="" width={32} height={32} />
                        </div>
                        <div className="branches-screen-empty-text">
                            <p className="branches-screen-empty-title">{t.branches.emptyTitle}</p>
                            <p className="branches-screen-empty-subtitle">{t.branches.emptySubtitle}</p>
                        </div>
                    </div>
                )}

                {!loading && !error && branches.length > 0 && (
                    <div className="branches-screen-grid">
                        {branches.map((branch, i) => (
                            <BranchCard
                                key={branch._id}
                                variant="configured"
                                branch={branch}
                                index={i}
                                onPreview={() => navigate(`/branches/${branch._id}`)}
                                onReset={canEditBranch(branch._id) ? () => handleResetPrompt(branch) : undefined}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
